import { GetterTree, MutationTree, ActionTree, ActionContext, Module } from 'vuex';
import { RootState } from '@/store/types';
import { ApplicantCustomStyle } from '@/api/auth';
import { colorStringToHsl, colorHslToStringWithLuminanceDecrease } from '@/util/color';
import { OsType, getOSType } from '@/util/device';
import { CustomStyleKey, customStyleKeys, propertyRules, toCssVariable } from '@/util/customStyle';
import { Locale } from '@/store/modules/types';

type State = {
  currentFontFamily: string;
  launchText: string;
  launchGradientTop: string;
  launchGradientBottom: string;
  maintenanceLogo: string;
  maintenanceGradientTop: string;
  maintenanceGradientBottom: string;
  errorHeaderText: string;
  errorHeaderColor: string;
  buttonFocus: string;
  typicalButtonText: string;
  typicalButtonEnabled: string;
  typicalButtonHover: string;
  typicalButtonActive: string;
  typicalButtonDisabled: string;
  negativeButtonEnabled: string;
  negativeButtonHover: string;
  negativeButtonActive: string;
  danger: string;
  dangerPale: string;
  dangerLight: string;
  shootingButtonHover: string;
  shootingButtonActive: string;
} & Record<CustomStyleKey, string | undefined>;

function createState(): State {
  const state: State = {} as State;
  clearState(state);
  return state;
}

function clearState(state: State): void {
  const isPC: boolean = getOSType() === OsType.PC;
  state.currentFontFamily = 'var(--typical-header-font-family)';
  state.launchText = isPC ? '#ffffff' : '#777777'; // 青グラデーション時代の色は '#ffffff'
  state.launchGradientTop = isPC ? '#d8d8d8' : '#ffffff'; // 青グラデーション時代の色は '#f3edd9';
  state.launchGradientBottom = isPC ? '#d8d8d8' : '#ffffff'; // 青グラデーション時代の色は #1bb0d1
  state.maintenanceLogo = '#777777'; // 青グラデーション時代の色は '#ffffff'
  state.maintenanceGradientTop = '#f5f5f5'; // 青グラデーション時代の色は '#f3edd9';
  state.maintenanceGradientBottom = '#f5f5f5'; // 青グラデーション時代の色は #1bb0d1
  state.errorHeaderText = '#333333';
  state.errorHeaderColor = '#dddddd';
  state.buttonFocus = 'tomato';
  state.typicalButtonText = '#ffffff';
  state.typicalButtonEnabled = '#1bb0d1';
  state.typicalButtonHover = '#189eb9';
  state.typicalButtonActive = '#158aa2';
  state.typicalButtonDisabled = '#dddddd';
  state.negativeButtonEnabled = '#0590ae';
  state.negativeButtonHover = '#047c94';
  state.negativeButtonActive = '#03677b';
  state.danger = '#ff4b00';
  state.dangerPale = '#f4B0b0';
  state.dangerLight = '#fff3f3';
  state.shootingButtonHover = '#f2f2f2';
  state.shootingButtonActive = '#e5e5e5';

  /**
   * UIカスタマイズ
   * @see https://liquidinc.atlassian.net/browse/EKYCDEV-3636
   */
  customStyleKeys.forEach((key: CustomStyleKey): void => {
    // CSS変数のデフォルト値のために@propertyを適用する
    const rule = propertyRules[key];
    try {
      // @ts-ignore
      CSS.registerProperty({
        name: toCssVariable(key as CustomStyleKey),
        syntax: rule.syntax,
        inherits: true,
        initialValue: rule.default
      });
    } catch (e) {
      // 既に登録されている場合は何もしない
    }

    //ここに登録しなくてもCSS.registerPropertyで登録されるが、FireFoxでまだCSS.registerPropertyが使えないため登録しておく
    state[key] = rule.default;
  });
}

const getters: GetterTree<State, RootState> = {};

const mutations: MutationTree<State> = {
  setStyles(state: State, payload: ApplicantCustomStyle[]) {
    payload.forEach((applicantCustomColor: ApplicantCustomStyle): void => {
      if (applicantCustomColor.key === 'typicalButtonActiveColor') {
        // ボタンの色を元にボタンのhover, active, disabledの色を設定
        const colorString: string = applicantCustomColor.value;
        const colorHsl: [number, number, number] = colorStringToHsl(colorString);

        state.typicalButtonEnabled = colorString;
        state.typicalButtonHover = colorHslToStringWithLuminanceDecrease(colorHsl, 1);
        state.typicalButtonActive = colorHslToStringWithLuminanceDecrease(colorHsl, 2);
        state.negativeButtonEnabled = colorHslToStringWithLuminanceDecrease(colorHsl, 2);
        state.negativeButtonHover = colorHslToStringWithLuminanceDecrease(colorHsl, 3);
        state.negativeButtonActive = colorHslToStringWithLuminanceDecrease(colorHsl, 4);
      }

      if (applicantCustomColor.key === 'typicalButtonInactiveColor') {
        const colorString: string = applicantCustomColor.value;
        state.typicalButtonDisabled = colorString;
      }

      if (applicantCustomColor.key === 'shootingButtonColor') {
        // 撮影ボタンの色を元にボタンのhover, activeの色を設定
        const colorString: string = applicantCustomColor.value;
        const colorHsl: [number, number, number] = colorStringToHsl(colorString);

        state.shootingButtonHover = colorHslToStringWithLuminanceDecrease(colorHsl, 1);
        state.shootingButtonActive = colorHslToStringWithLuminanceDecrease(colorHsl, 2);
      }

      // 汎用書類・追加書類のチェックボックスの色を通常のチェックボックスと同じ色に設定
      // 通常チェックボックスとはデフォルトカラーが異なるので項目を分けている
      if (applicantCustomColor.key === 'confrimationCheckboxActiveIconColor')
        state.confrimationGpOpCheckboxActiveIconColor = applicantCustomColor.value;

      if (applicantCustomColor.key === 'confrimationCheckboxInactiveIconColor')
        state.confrimationGpOpCheckboxInactiveIconColor = applicantCustomColor.value;

      state[applicantCustomColor.key] = applicantCustomColor.value;
    });
  },

  setPaidyStyle(state: State) {
    state.shootingIconColor = '#FFFFFF';
    state.shootingFrameImageColor = '#e30e80';
    state.shootingFrameBorderColor = '#e30e80';
    state.shootingDiagonalIllustrationColor = '#e30e80';
    state.actionShootIconCompleteColor = '#51C586';
    state.actionShootIconArrowColor = '#E5258C';
    state.actionShootFrameBorderColor = '#E5258C';
    state.modalLeftButtonColor = 'white';
    state.modalLeftButtonFontWeight = 'bold';
    state.typicalButtonInactiveColor = '#cfd4d8';
    state.typicalButtonActiveColor = 'linear-gradient(135deg, #e5258c 0%, #823cd7 100%)';
    state.typicalButtonHover = 'white';
    state.typicalButtonActive = 'white';
    state.typicalButtonInactiveFontSizePc = '16px';
    state.typicalButtonInactiveFontSizeSp = '16px';
    state.typicalButtonActiveFontSizePc = '16px';
    state.typicalButtonActiveFontSizeSp = '16px';
    state.typicalButtonInactiveFontWeight = 'bold';
    state.typicalButtonActiveFontWeight = 'bold';
  },

  setStyle(state: State, payload: ApplicantCustomStyle) {
    state[payload.key] = payload.value;
  },

  clearState(state: State) {
    clearState(state);
  }
};

const actions: ActionTree<State, RootState> = {
  clearState(context: ActionContext<State, RootState>): void {
    context.commit('clearState');
  },

  setApplicantCustomStyles(context: ActionContext<State, RootState>, payload: ApplicantCustomStyle[]): void {
    context.commit('clearState');
    context.commit('setStyles', payload);
  },

  setPaidyStyle(context: ActionContext<State, RootState>): void {
    // LaunchScreen.vueにてsetApplicantCustomStylesの後に呼ぶのでclearStateしない
    context.commit('setPaidyStyle');
  },

  setFontFamily(context: ActionContext<State, RootState>, payload: Locale): void {
    const fontFamilies: Record<Locale, ApplicantCustomStyle['key']> = {
      '': 'typicalHeaderFontFamily',
      ja: 'typicalHeaderFontFamily',
      en: 'typicalHeaderFontFamily',
      id: 'typicalHeaderFontFamily',
      th: 'typicalHeaderFontFamilyThai',
      vi: 'typicalHeaderFontFamilyVietnamese',
      zh: 'typicalHeaderFontFamilySimplifiedChinese',
      ms: 'typicalHeaderFontFamily'
    };

    context.commit('setStyle', { key: 'currentFontFamily', value: `var(${toCssVariable(fontFamilies[payload])})` });
  }
};

const module: Module<State, RootState> = {
  namespaced: true,
  state: createState,
  getters,
  mutations,
  actions
};

export default module;
